import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ButtonComp, CardComp, TableCompData } from "../../../components";
import PageWrapper from "../modules/PageWrapper";
import { formatMoney } from "../../../utils/currencyFormat";
import {
  BsCalendar2Check,
  BsCalendar2X,
  BsWallet2,
  BsInfoCircle,
  BsDownload,
} from "react-icons/bs";
import moment from "moment";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import EmptyTable from "../../../components/Common/EmptyTable";
import TablePagination from "../../../components/Table/Pagination";
import { toast } from "react-hot-toast";
import {
  useGetMemberAttendanceSummaryQuery,
  useGetMemberAttendanceHistoryQuery,
  usePayAttendanceFineMutation,
  usePayAttendanceFineBankTransferMutation,
} from "../../../store/member/member-attendance";
import { useGetMemberCompanyBanksQuery } from "../../../store/member/member-contribution";
import MemberPendingBankTransfersCard from "../modules/MemberPendingBankTransfersCard";
import MemberPaymentModeChooser from "../modules/MemberPaymentModeChooser";
import { selectToken } from "../../../store/auth";
import {
  getFrontendBaseUrl,
  isPaystackMode,
  isSocietyBankMode,
  redirectToMemberOnlinePayment,
} from "../../../utils/memberOnlinePayment";

/**
 * MemberAttendance
 *
 * Completely redesigned member-side attendance management focusing on:
 * 1. Financial transparency (Fines)
 * 2. Ease of payment
 * 3. Standard practices (Pagination, Loading States, Filters, Exports)
 */
export default function MemberAttendance() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const limit = 20;
  const token = useSelector(selectToken);

  // Real Data Fetching
  const {
    data: summary,
    isLoading: isLoadingSummary,
  } = useGetMemberAttendanceSummaryQuery();
  const {
    data: historyData,
    isFetching: isFetchingHistory,
    isLoading: isLoadingHistory,
  } = useGetMemberAttendanceHistoryQuery({
    page,
    limit,
    status: statusFilter,
    from_date: fromDate,
    to_date: toDate,
  });

  const [payFine, { isLoading: isPayingFine }] = usePayAttendanceFineMutation();
  const [payFineBank, { isLoading: isPayingFineBank }] =
    usePayAttendanceFineBankTransferMutation();
  const { data: companyBanks = [] } = useGetMemberCompanyBanksQuery();
  const [finePaymentMode, setFinePaymentMode] = useState("paystack");
  const [fineBankId, setFineBankId] = useState("");
  const [fineBankNote, setFineBankNote] = useState("");

  const handleFinePaymentModeChange = useCallback((mode) => {
    setFinePaymentMode(mode);
    if (isSocietyBankMode(mode)) {
      return;
    }
    setFineBankId("");
    setFineBankNote("");
  }, []);

  const history = historyData?.history || [];
  const pagination = historyData?.pagination || {
    currentPage: page,
    totalPages: 1,
    total: 0,
    perPage: limit,
    count: 0,
  };

  const handlePayFines = async () => {
    try {
      const result = await payFine({
        amount: summary?.unpaidFines,
        payment_provider: isPaystackMode(finePaymentMode) ? "paystack" : "alatpay",
        payment_method: isPaystackMode(finePaymentMode) ? "PAYSTACK" : "ALATPAY",
        frontend_base_url: getFrontendBaseUrl(),
      }).unwrap();

      if (result.status && (result.data?.authorization_url || result.data?.payment_url)) {
        toast.success("Redirecting to payment page...");
        if (!redirectToMemberOnlinePayment(result)) {
          toast.error("Payment URL not received. Please try again.");
        }
      } else {
        toast.error(result.message || "Failed to initiate payment");
      }
    } catch (error) {
      toast.error(error?.data?.message || "An error occurred during payment");
    }
  };

  const handlePayFinesBankTransfer = async () => {
    if (!fineBankId) {
      toast.error("Please select the society account you paid into");
      return;
    }
    try {
      const result = await payFineBank({
        company_bank_id: parseInt(fineBankId, 10),
        member_note: fineBankNote?.trim() || undefined,
      }).unwrap();
      if (!result?.status) {
        toast.error(result?.message || "Request failed");
        return;
      }
      toast.success(
        "Transfer request recorded. Pay the exact fine amount; staff will confirm after verifying your deposit."
      );
      setFineBankId("");
      setFineBankNote("");
    } catch (error) {
      toast.error(
        error?.data?.message ||
          error?.message ||
          "An error occurred. Please try again."
      );
    }
  };

  const handleDownloadReport = () => {
    const baseUrl =
      process.env.REACT_APP_BASE_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      "http://localhost:8091/api/v1";
    const params = new URLSearchParams({
      guard: "member",
      status: statusFilter,
      from_date: fromDate || "",
      to_date: toDate || "",
      format: "csv",
    });
    const url = `${baseUrl}/members/attendance/download?${params.toString()}`;

    toast.loading("Preparing your attendance report...", {
      id: "download-toast",
    });

    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to download report");
        }
        return response.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `Attendance_Report_${
          new Date().toISOString().split("T")[0]
        }.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        toast.success("Attendance report downloaded successfully!", {
          id: "download-toast",
        });
      })
      .catch((err) => {
        console.error("Download failed", err);
        toast.error(
          err.message || "Failed to download report. Please try again.",
          { id: "download-toast" },
        );
      });
  };

  // Summary Skeleton Card
  const SummarySkeleton = () => (
    <div className="col-lg-3 col-sm-6">
      <CardComp cardClassName="h-100 border-0 shadow3 text-center py-4 rounded-4">
        <div
          className="bg-light p-3 rounded-circle d-inline-block mb-3 animate-pulse"
          style={{ width: "60px", height: "60px", background: "#f1f1f1" }}
        />
        <div
          className="h-4 bg-light rounded w-50 mx-auto mb-2 animate-pulse"
          style={{ height: "24px", background: "#e5e5e5" }}
        />
        <div
          className="h-2 bg-light rounded w-30 mx-auto animate-pulse"
          style={{ height: "12px", background: "#f1f1f1" }}
        />
      </CardComp>
    </div>
  );

  const columns = [
    {
      name: "Date",
      selector: (row) => (
        <div className="py-2">
          <div
            className="fw-bold"
            style={{ fontFamily: "'Work Sans', sans-serif" }}
          >
            {moment(row.date).format("DD-MMM-YYYY")}
          </div>
          <small className="text-muted" style={{ fontSize: "11px" }}>
            {moment(row.date).format("dddd")}
          </small>
        </div>
      ),
      width: "150px",
    },
    {
      name: "Meeting / Event",
      selector: (row) => (
        <span className="fw-medium text-black1">{row.name}</span>
      ),
      minWidth: "250px",
    },
    {
      name: "Attendance Status",
      selector: (row) => {
        const statusColors = {
          1: { bg: "#19BC31" }, // Present Early
          2: { bg: "#FFA500" }, // Late
          3: { bg: "var(--color-secondary)" }, // Approved Absent
          4: { bg: "#FF4343" }, // Non-Approved
        };
        const style = statusColors[row.status_id] || { bg: "#A1A0A0" };
        return (
          <span
            className="px-3 py-1 rounded-pill text-white fw-medium text-center shadow-sm"
            style={{
              backgroundColor: style.bg,
              fontSize: "12px",
              minWidth: "140px",
              display: "inline-block",
              fontFamily: "'Work Sans', sans-serif",
            }}
          >
            {row.status}
          </span>
        );
      },
      minWidth: "180px",
    },
    {
      name: "Fine Details",
      selector: (row) => {
        if (!row.fine) return <span className="text-muted small">-</span>;
        const isPaid = row.fine.status === "PAID";
        return (
          <div className="d-flex align-items-center gap-2">
            <span
              className="fw-bold text-black1"
              style={{ fontFamily: "'Work Sans', sans-serif" }}
            >
              {formatMoney(row.fine.amount)}
            </span>
            <span
              className="px-2 py-1 rounded text-white fw-bold shadow-sm"
              style={{
                backgroundColor: isPaid ? "#19BC31" : "#FFA500",
                fontSize: "10px",
                minWidth: "65px",
                textAlign: "center",
                fontFamily: "'Work Sans', sans-serif",
              }}
            >
              {row.fine.status}
            </span>
          </div>
        );
      },
      minWidth: "150px",
    },
  ];

  return (
    <PageWrapper title={"Attendance & Fines"}>
      <MemberPendingBankTransfersCard flowFilter="fine" />

      {/* 1. Summary Cards */}
      <section className="row g-4 mb-5" data-tour="member-attendance-summary">
        {isLoadingSummary ? (
          <>
            <SummarySkeleton />
            <SummarySkeleton />
            <SummarySkeleton />
            <SummarySkeleton />
            <SummarySkeleton />
          </>
        ) : (
          <>
            <div className="col-lg-3 col-sm-6">
              <CardComp cardClassName="h-100 border-0 shadow3 text-center py-4 rounded-4">
                <div
                  className="bg-success bg-opacity-10 p-3 rounded-circle d-inline-block mb-3"
                  style={{ width: "60px", height: "60px" }}
                >
                  <BsCalendar2Check style={{ color: "#19BC31" }} size={24} />
                </div>
                <h2
                  className="fw-bold mb-1"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    color: "#383838",
                  }}
                >
                  {summary?.totalPresent || 0}
                </h2>
                <p
                  className="text-muted small mb-0 fw-medium"
                  style={{ fontFamily: "'Work Sans', sans-serif" }}
                >
                  Days Present
                </p>
              </CardComp>
            </div>
            <div className="col-lg-3 col-sm-6">
              <CardComp cardClassName="h-100 border-0 shadow3 text-center py-4 rounded-4">
                <div
                  className="bg-danger bg-opacity-10 p-3 rounded-circle d-inline-block mb-3"
                  style={{ width: "60px", height: "60px" }}
                >
                  <BsCalendar2X style={{ color: "#FF4343" }} size={24} />
                </div>
                <h2
                  className="fw-bold mb-1"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    color: "#383838",
                  }}
                >
                  {summary?.totalAbsent || 0}
                </h2>
                <p
                  className="text-muted small mb-0 fw-medium"
                  style={{ fontFamily: "'Work Sans', sans-serif" }}
                >
                  Days Absent
                </p>
              </CardComp>
            </div>
            <div className="col-lg-3 col-sm-6">
              <CardComp cardClassName="h-100 border-0 shadow3 text-center py-4 rounded-4">
                <div
                  className="bg-info bg-opacity-10 p-3 rounded-circle d-inline-block mb-3"
                  style={{ width: "60px", height: "60px" }}
                >
                  <BsInfoCircle style={{ color: "var(--color-secondary)" }} size={24} />
                </div>
                <h2
                  className="fw-bold mb-1"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    color: "#383838",
                  }}
                >
                  {summary?.attendanceRate || 0}%
                </h2>
                <p
                  className="text-muted small mb-0 fw-medium"
                  style={{ fontFamily: "'Work Sans', sans-serif" }}
                >
                  Overall Rate
                </p>
              </CardComp>
            </div>
            <div className="col-lg-3 col-sm-6">
              <CardComp cardClassName="h-100 border-0 shadow3 text-center py-4 rounded-4">
                <div
                  className="bg-warning bg-opacity-10 p-3 rounded-circle d-inline-block mb-3"
                  style={{ width: "60px", height: "60px" }}
                >
                  <BsWallet2 style={{ color: "#FFA500" }} size={24} />
                </div>
                <h2
                  className="fw-bold mb-1 text-warning"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    color: "#FFA500",
                  }}
                >
                  {formatMoney(summary?.unpaidFines || 0)}
                </h2>
                <p
                  className="text-muted small mb-0 fw-medium"
                  style={{ fontFamily: "'Work Sans', sans-serif" }}
                >
                  Unpaid Fines
                </p>
              </CardComp>
            </div>
            <div className="col-lg-3 col-sm-6">
              <CardComp cardClassName="h-100 border-0 shadow3 text-center py-4 rounded-4">
                <div
                  className="bg-success bg-opacity-10 p-3 rounded-circle d-inline-block mb-3"
                  style={{ width: "60px", height: "60px" }}
                >
                  <BsWallet2 style={{ color: "#19BC31" }} size={24} />
                </div>
                <h2
                  className="fw-bold mb-1"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    color: "#19BC31",
                  }}
                >
                  {formatMoney(summary?.totalPaidFines || 0)}
                </h2>
                <p
                  className="text-muted small mb-0 fw-medium"
                  style={{ fontFamily: "'Work Sans', sans-serif" }}
                >
                  Total Fines Paid
                </p>
              </CardComp>
            </div>
          </>
        )}
      </section>

      {/* 2. Primary CTA Card for Fines */}
      {!isLoadingSummary && summary?.unpaidFines > 0 && (
        <section className="mb-5" data-tour="member-attendance-fines">
          <div
            className="p-4 rounded-4 shadow3 border-0 d-flex justify-content-between align-items-center flex-wrap gap-4"
            style={{
              background: "linear-gradient(135deg, var(--color-primary) 0%, color-mix(in srgb, var(--color-primary) 80%, black) 100%)",
              color: "#fff",
            }}
          >
            <div className="d-flex align-items-center gap-4">
              <div
                className="bg-white bg-opacity-20 p-3 rounded-3 d-flex align-items-center justify-content-center"
                style={{ width: "64px", height: "64px" }}
              >
                <BsWallet2 size={32} className="text-white" />
              </div>
              <div>
                <h4
                  className="fw-bold mb-1 text-white"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  Outstanding Fines
                </h4>
                <p
                  className="mb-0 opacity-75 small"
                  style={{ fontFamily: "'Work Sans', sans-serif" }}
                >
                  You have {formatMoney(summary?.unpaidFines)} in unpaid
                  attendance fines. Settle now to maintain your benefits.
                </p>
              </div>
            </div>
            <div className="d-flex flex-column align-items-stretch gap-3 flex-grow-1 flex-md-grow-0 w-100" style={{ maxWidth: "420px" }}>
              <div className="text-md-end text-center text-md-start">
                <small
                  className="opacity-75 d-block text-uppercase fw-bold"
                  style={{
                    fontSize: "10px",
                    letterSpacing: "1px",
                    fontFamily: "'Work Sans', sans-serif",
                  }}
                >
                  Payable Amount
                </small>
                <h3
                  className="fw-bold mb-0"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {formatMoney(summary?.unpaidFines)}
                </h3>
              </div>
              <div className="d-flex flex-column gap-2">
                <div className="p-3 rounded-3 bg-white text-dark">
                  <MemberPaymentModeChooser
                    paymentMode={finePaymentMode}
                    onPaymentModeChange={handleFinePaymentModeChange}
                    companyBanks={companyBanks}
                    selectedBankId={fineBankId}
                    onSelectedBankIdChange={setFineBankId}
                    memberNote={fineBankNote}
                    onMemberNoteChange={setFineBankNote}
                    compact
                  />
                </div>
              </div>
              <ButtonComp
                btnText={
                  isSocietyBankMode(finePaymentMode)
                    ? isPayingFineBank
                      ? "Submitting..."
                      : "Submit transfer request"
                    : "Pay Fines Now"
                }
                btnClassName={
                  "bg-white text-dark py-3 px-5 fw-bold rounded-3 border-0 shadow-sm"
                }
                style={{ fontFamily: "'DM Sans', sans-serif" }}
                loading={isSocietyBankMode(finePaymentMode) ? isPayingFineBank : isPayingFine}
                disabled={
                  (!isSocietyBankMode(finePaymentMode) && isPayingFine) ||
                  (isSocietyBankMode(finePaymentMode) && (isPayingFineBank || !fineBankId))
                }
                onClick={
                  isSocietyBankMode(finePaymentMode)
                    ? handlePayFinesBankTransfer
                    : handlePayFines
                }
              />
            </div>
          </div>
        </section>
      )}

      {/* 3. History Table Section */}
      <section className="mb-5" data-tour="member-attendance-history">
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-4 gap-3">
          <div>
            <h4
              className="fw-bold text-black1 mb-1"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Attendance Record
            </h4>
            <p
              className="text-muted small mb-0"
              style={{ fontFamily: "'Work Sans', sans-serif" }}
            >
              Complete history of meeting attendance and applied fines.
            </p>
          </div>
          <div className="d-flex gap-3 align-items-center flex-wrap">
            <div className="d-flex gap-2 align-items-center">
              <div style={{ maxWidth: "160px" }}>
                <input
                  type="date"
                  className="form-control py-2 px-3 bg-white border shadow-sm small fw-medium"
                  style={{
                    borderRadius: "10px",
                    fontFamily: "'Work Sans', sans-serif",
                  }}
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setPage(1);
                  }}
                  title="From Date"
                />
              </div>
              <span className="text-muted small">to</span>
              <div style={{ maxWidth: "160px" }}>
                <input
                  type="date"
                  className="form-control py-2 px-3 bg-white border shadow-sm small fw-medium"
                  style={{
                    borderRadius: "10px",
                    fontFamily: "'Work Sans', sans-serif",
                  }}
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setPage(1);
                  }}
                  title="To Date"
                />
              </div>
            </div>
            <div style={{ minWidth: "180px" }}>
              <select
                className="form-select py-2 px-3 bg-white border shadow-sm small fw-medium"
                style={{
                  borderRadius: "10px",
                  fontFamily: "'Work Sans', sans-serif",
                }}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="all">All Statuses</option>
                <option value="present">Present Only</option>
                <option value="absent">Absent Only</option>
                <option value="fined">With Fines</option>
              </select>
            </div>
            <ButtonComp
              btnText={"Download"}
              btnClassName={
                "bg-white text-primary1 border border-primary1 py-2 px-3 rounded shadow-sm d-flex align-items-center gap-2 small fw-bold"
              }
              style={{
                fontFamily: "'Work Sans', sans-serif",
                minWidth: "fit-content",
              }}
              icon={<BsDownload size={14} />}
              onClick={handleDownloadReport}
            />
          </div>
        </div>

        <TableCompData
          columns={columns}
          data={history}
          marginBottom={"1rem"}
          progressPending={isFetchingHistory || isLoadingHistory}
          progressComponent={<TableSkeletonLoader rows={8} columns={4} />}
          message={
            !isFetchingHistory && !isLoadingHistory && history.length === 0 ? (
              <EmptyTable Message="No attendance records found for your account." />
            ) : null
          }
        />

        {/* Pagination */}
        {!isLoadingHistory && pagination && pagination.total > 0 && (
          <div className="mt-4">
            <TablePagination
              marginTop={"1rem"}
              handlePageChange={setPage}
              currentPage={pagination.currentPage || page}
              totalPages={pagination.totalPages || 1}
              count={pagination.count || history.length || 0}
              total={pagination.total || 0}
              perPage={pagination.perPage || 20}
            />
          </div>
        )}
      </section>

      {/* 4. Transparency & Help Section */}
      <section className="row g-4">
        <div className="col-md-8">
          <div className="p-4 bg-light rounded-4 border d-flex align-items-start gap-3 shadow-sm h-100">
            <div className="bg-white p-2 rounded-circle border shadow-sm">
              <BsInfoCircle style={{ color: "var(--color-primary)" }} size={20} />
            </div>
            <div>
              <h6
                className="fw-bold mb-2"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  color: "#383838",
                }}
              >
                Our Fair Attendance Policy
              </h6>
              <p
                className="mb-0 small text-muted lh-base"
                style={{ fontFamily: "'Work Sans', sans-serif" }}
              >
                Fines are only applied for{" "}
                <strong>Non-Approved Absences</strong>. If you believe a status
                was marked in error, please contact your branch admin. Once a
                status is corrected from Absent to Present, any{" "}
                <strong>paid fine</strong> will be automatically reversed and
                credited back to your wallet balance.
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <CardComp
            cardClassName="h-100 border-0 shadow3 p-4 rounded-4"
            style={{ backgroundColor: "var(--color-primary-soft)" }}
          >
            <h6
              className="fw-bold mb-3"
              style={{ fontFamily: "'DM Sans', sans-serif", color: "#383838" }}
            >
              Need Assistance?
            </h6>
            <p
              className="small text-muted mb-4"
              style={{ fontFamily: "'Work Sans', sans-serif" }}
            >
              If you have questions about your attendance record or a specific
              fine, our support team is here to help.
            </p>
            <ButtonComp
              btnText={"Contact Admin"}
              btnClassName={
                "w-100 py-2 rounded-3 text-white border-0 fw-medium"
              }
              style={{
                backgroundColor: "var(--color-primary)",
                fontFamily: "'DM Sans', sans-serif",
              }}
              onClick={() => navigate("/member/support")}
            />
          </CardComp>
        </div>
      </section>
    </PageWrapper>
  );
}
